import { Component, forwardRef, Input, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownModule } from 'primeng/dropdown';
import { takeUntil } from 'rxjs';
import { HeadquartersService } from 'src/app/shared/api-services/headquarters.service';
import { LocationModel } from 'src/app/shared/models/location/location';
import { BaseSelectComponent } from '../base-select/base-select.component';
import { CommonModule } from '@angular/common';
import { LocationFilter } from 'src/app/shared/models/location/location-filter';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'location-select',
  imports: [
    TranslateModule,
    DropdownModule,
    CommonModule,
    FormsModule
  ],
  template: `
    <!-- PrimeNG Dropdown per desktop -->
    <p-dropdown
      *ngIf="!isMobile"
      class="w-100"
      [placeholder]="placeholder | translate"
      [(ngModel)]="selectedDropdownValue"
      [options]="listItems"
      [showClear]="true"
      [filter]="true"
      [filterBy]="'name'"
      (onFilter)="getItems($event.filter)"
      (onChange)="handleDropdownChange($event.value)"
      (onShow)="onDropdownShow()"
      [ngClass]="{'has-value': hasValue}"
      optionLabel="name"
      [loading]="isLoading"
      [disabled]="disabled"
      [dataKey]="'item.id'">
    </p-dropdown>

    <!-- Select nativo per mobile -->
    <select
      *ngIf="isMobile"
      class="form-control mobile-select w-100"
      [disabled]="disabled"
      (change)="onNativeSelectChange($event)"
      (focus)="onNativeSelectFocus()">
      <option value="">{{ placeholder | translate }}</option>
      <option *ngFor="let option of listItems" [value]="option.item.id" [selected]="selectedDropdownValue?.item?.id === option.item.id">
        {{ option.name }}
      </option>
    </select>
  `,
  styleUrls: ['../base-select/base-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSelectComponent),
      multi: true
    }
  ],
  standalone: true
})
export class LocationSelectComponent extends BaseSelectComponent<LocationModel, void> implements OnChanges, OnInit {
  @Input() locationId: string | null = null;
  @Input() headquarterId: string | null = null;
  @Input() headquarterIds: string[] | null = null;
  @Input() disabled: boolean = false;
  @Input() ids: string[] | null = null;

  selectedDropdownValue: { name: string, item: LocationModel } | null = null;

  constructor(
    private _headquartersService: HeadquartersService,
    breakpointObserver: BreakpointObserver
  ) {
    super(breakpointObserver);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    
    // Se siamo in modalità mobile e non stiamo già caricando un elemento specifico tramite locationId,
    // precarica la lista all'inizializzazione per evitare il select vuoto
    if (this.isMobile && !this.locationId) {
      this.getItems('');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Se l'ID della location è presente e cambia, otteniamo i dettagli della location
    if (changes['locationId'] && this.locationId) {
      this.fetchAndSetInitialLocation(this.locationId);
    } 
    // Se cambia l'headquarterId, dobbiamo verificare se la nostra location attuale è valida
    else if (changes['headquarterId'] && changes['headquarterId'].previousValue !== changes['headquarterId'].currentValue) {
      // Se abbiamo sia headquarterId che locationId, dobbiamo verificare se la location appartiene all'headquarter
      if (this.headquarterId && this.selectedDropdownValue?.item?.id) {
        // Verifica se la location appartiene all'headquarter selezionato
        this._headquartersService.getLocation$(this.selectedDropdownValue.item.id)
          .pipe(takeUntil(this._destroy$))
          .subscribe({
            next: (location) => {
              // Se la location esiste ma non appartiene all'headquarter corrente, resettiamo la selezione
              if (location && location.headquarterId !== this.headquarterId) {
                this.onClearItem();
                this.getItems('');
              } else if (location) {
                // Se la location appartiene all'headquarter, manteniamo la selezione
                this.getItems('');
              }
            },
            error: () => {
              // In caso di errore, resettiamo e ricarichiamo
              this.onClearItem();
              this.getItems('');
            }
          });
      } else {
        // Se non abbiamo una location selezionata, ricarichiamo semplicemente la lista
        this.getItems('');
      }
    } 
    // Nei casi in cui dobbiamo solo aggiornare la lista
    else if ((changes['locationId'] && !this.locationId) || 
             changes['ids'] || changes['headquarterIds']) {
      this.getItems('');
    }
  }

  fetchAndSetInitialLocation(locationId: string): void {
    this.isLoading = true;
    this._headquartersService.getLocation$(locationId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (location) => {
          this.isLoading = false;
          if (location) {
            this.writeValue(location);

            const currentOptionInList = this.listItems.find(opt => opt.item.id === location.id);
            if (!currentOptionInList) {
              this.listItems = [{ name: location.name || '', item: location }, ...this.listItems.filter(opt => opt.item.id !== location.id)];
            }
          }
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  override onSelectItem(item: LocationModel): void {
    super.onSelectItem(item);
    if (item) {
      this.selectedDropdownValue = { name: item.name || '', item: item };
    }
  }

  override onClearItem(): void {
    super.onClearItem();
    this.selectedDropdownValue = null;
  }

  handleDropdownChange(selectedOptionFromDropdown: { name: string, item: LocationModel } | null): void {
    if (selectedOptionFromDropdown) {
      super.onSelectItem(selectedOptionFromDropdown.item);
    } else {
      super.onClearItem();
    }
  }

  override getItems(query: string): void {
    this.isLoading = true;
    const filter: LocationFilter = {
      listIds: this.ids ? this.ids.join(',') : null,
      listHeadquarterIds: this.headquarterIds ? this.headquarterIds.join(',') : null,
      headquarterId: this.headquarterId,
      nameContains: query,
      limit: this.limit,
      orderBy: 'Name'
    };

    this._headquartersService.listLocations$(filter)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          this.listItems = response.items.map((location) => ({ name: location.name || '', item: location }));
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }
} 